class BasicHeader extends HTMLElement {
  constructor() {
    super()
  }

  get headerSection () {
    return document.querySelector('.header-section')
  }

  get enableTransparent () {
    return this.dataset.enableTransparent === 'true'
  }

  connectedCallback () {
    this.init()
    if (window.ResizeObserver) {
      new ResizeObserver(this.setHeight.bind(this)).observe(this)
    }

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section')
      section.addEventListener('shopify:section:load', this.init.bind(this))
      section.addEventListener('shopify:section:unload', this.init.bind(this))
      section.addEventListener('shopify:section:reorder', this.init.bind(this))
    }
  }

  init () {
    new FoxTheme.delayUntilInteraction(this.setHeight.bind(this))

    if (this.enableTransparent) {
      this.headerSection.classList.add('header-transparent')
    }
  }

  setHeight () {
    requestAnimationFrame(() => {
      // 缓存布局计算以减少重排
      const offsetHeight = Math.round(this.offsetHeight)
      const offsetTop = Math.round(this.parentElement.offsetTop)

      // 批量更新样式
      document.documentElement.style.setProperty('--header-height', `${offsetHeight}px`)
      document.documentElement.style.setProperty('--header-offset-top', `${offsetTop}px`)
    })
  }
}

customElements.define('basic-header', BasicHeader, { extends: 'header' })

class StickyHeader extends BasicHeader {
  constructor() {
    super()

    this.classes = {
      pinned: 'header-pinned',
    }
  }

  get isAlwaysSticky () {
    return this.dataset.stickyType === 'always'
  }

  get isHomepage () {
    // 使用 data-enable-transparent 判断是否需要首页同款透明头部效果
    return this.enableTransparent
  }

  connectedCallback () {
    super.connectedCallback()

    this.currentScrollTop = 0
    this.firstScrollTop = window.scrollY

    // 缓存初始边界以提高性能
    this.headerBounds = this.headerSection.getBoundingClientRect()

    // 缓存logo元素用于动画
    this.logoInMenu = document.querySelector('.header__logo-in-menu')

    // 缓存移动端头部中心元素用于平滑的顶部过渡
    this.headerMobileCenter = document.querySelector('.shopify-section-group-header-group .header-mobile--center')
    this.headerIconsRight = document.querySelector('.header__icons--right')

    // 缓存菜单项用于间距动画
    this.menuItems = document.querySelectorAll('.header__menu .menu__item')
    this.logoInMenuElement = document.querySelector('.header__logo-in-menu')
    // 缓存所有菜单列表项（包括logo）用于transform动画
    this.menuListItems = null
    this.logoIndex = null
    /** 低分辨率下第一个菜单项（如 PRODUCTS）的安全 translateX(px)，避免左侧裁切 */
    this.navFirstItemSafeTranslatePx = 0
    this.desktopHomepageScrollRange = 400
    this.desktopHeaderCenterOffset = 75
    this.desktopLogoStartOffsetY = -55

    this.isScrolled = false
    this.animationFrame = null

    this.beforeInit()
  }

  beforeInit () {
    this.headerSection.classList.add('header-sticky')
    this.headerSection.dataset.stickyType = this.dataset.stickyType

    // 初始化logo状态
    this.initializeLogos()

    window.addEventListener('scroll', this.onScrollHandler.bind(this), false)
    window.addEventListener('resize', this.updateNavOffsetForViewport.bind(this))
    requestAnimationFrame(() => this.updateNavOffsetForViewport())
  }

  /**
   * 根据视口宽度自动计算第一个菜单项的安全左偏移，避免低分辨率下被裁切
   */
  updateNavOffsetForViewport () {
    if (window.innerWidth < 1024) {
      this.resetDesktopHomepageScrollState()
      this.resetMenuItemsTransform()
      return
    }

    this.navFirstItemSafeTranslatePx = 0

    if (this.isHomepage) {
      this.applyDesktopHomepageScrollState(window.scrollY)
      return
    }

    this.resetDesktopHomepageScrollState()
    this.resetMenuItemsTransform()
  }

  isDesktopHomepageAnimationEnabled () {
    return this.isHomepage && window.innerWidth >= 1024
  }

  ensureDesktopHomepageElements () {
    if (!this.headerMobileCenter) {
      this.headerMobileCenter = document.querySelector('.shopify-section-group-header-group .header-mobile--center')
    }

    if (!this.logoInMenu) {
      this.logoInMenu = document.querySelector('.header__logo-in-menu')
    }

    if (!this.headerIconsRight) {
      this.headerIconsRight = document.querySelector('.header__icons--right')
    }
  }

  getScrollProgress (scrollTop) {
    return Math.min(Math.max(scrollTop / this.desktopHomepageScrollRange, 0), 1)
  }

  easeOutCubic (progress) {
    return 1 - ((1 - progress) ** 3)
  }

  applyDesktopHomepageScrollState (scrollTop) {
    this.ensureDesktopHomepageElements()

    const scrollProgress = this.getScrollProgress(scrollTop)
    const easedProgress = this.easeOutCubic(scrollProgress)
    const centerOffset = this.desktopHeaderCenterOffset * (1 - easedProgress)
    const logoOffsetY = this.desktopLogoStartOffsetY * (1 - easedProgress)

    if (this.headerMobileCenter) {
      this.headerMobileCenter.style.top = '0'
      this.headerMobileCenter.style.transform = `translate3d(0, ${centerOffset}px, 0)`
      this.headerMobileCenter.style.transition = 'none'
      this.headerMobileCenter.style.willChange = scrollProgress < 1 ? 'transform' : ''
    }

    if (this.logoInMenu) {
      this.logoInMenu.style.transform = `translate3d(0, ${logoOffsetY}px, 0)`
      this.logoInMenu.style.transition = 'none'
      this.logoInMenu.style.willChange = scrollProgress < 1 ? 'transform' : ''
    }

    this.updateMenuItemsTransform(easedProgress)

    return {
      scrollProgress,
      isTopReached: centerOffset <= 0.1,
    }
  }

  resetDesktopHomepageScrollState () {
    this.ensureDesktopHomepageElements()

    if (this.headerMobileCenter) {
      this.headerMobileCenter.style.top = '0'
      this.headerMobileCenter.style.transform = 'translate3d(0, 0, 0)'
      this.headerMobileCenter.style.transition = 'none'
      this.headerMobileCenter.style.willChange = ''
    }

    if (this.logoInMenu) {
      this.logoInMenu.style.transform = 'translate3d(0, 0, 0)'
      this.logoInMenu.style.transition = 'none'
      this.logoInMenu.style.willChange = ''
    }

    if (this.headerIconsRight) {
      this.headerIconsRight.style.transform = 'translate3d(0, 0, 0)'
      this.headerIconsRight.style.transition = 'none'
      this.headerIconsRight.style.willChange = ''
    }
  }

  resetMenuItemsTransform () {
    if (!this.menuListItems || this.logoIndex === null || this.logoIndex === -1) {
      this.initializeMenuItemsTransform(1)
    }

    if (!this.menuListItems) return

    this.menuListItems.forEach((li) => {
      if (li.classList.contains('header__logo-in-menu')) return

      li.style.transform = 'translateX(0)'
      li.style.transition = 'none'
      li.style.marginLeft = '0px'
    })

    if (this.logoInMenuElement) {
      this.logoInMenuElement.style.marginLeft = '0'
      this.logoInMenuElement.style.marginRight = '0'
      this.logoInMenuElement.style.transition = 'none'
    }

    if (this.headerIconsRight) {
      this.headerIconsRight.style.transform = 'translate3d(0, 0, 0)'
      this.headerIconsRight.style.transition = 'none'
      this.headerIconsRight.style.willChange = ''
    }
  }

  initializeLogos () {
    const scrollTop = window.scrollY
    const headerBoundsTop = this.headerBounds.top + this.firstScrollTop
    const isScrolled = scrollTop > headerBoundsTop
    const isTopReached = this.isDesktopHomepageAnimationEnabled()
      ? this.applyDesktopHomepageScrollState(scrollTop).isTopReached
      : (() => {
          this.resetDesktopHomepageScrollState()
          this.resetMenuItemsTransform()
          return isScrolled
        })()

    this.isScrolled = isScrolled

    if (isScrolled && isTopReached) {
      this.headerSection.classList.add('header-scrolled')
    } else {
      this.headerSection.classList.remove('header-scrolled')
    }
  }

  onScrollHandler () {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame)
    }

    this.animationFrame = requestAnimationFrame(() => {
      const scrollTop = window.scrollY
      const headerSection = this.headerSection
      const headerBoundsTop = this.headerBounds.top + this.firstScrollTop
      const headerBoundsBottom = this.headerBounds.bottom + this.firstScrollTop
      const shouldBeScrolled = scrollTop > headerBoundsTop
      const isTopReached = this.isDesktopHomepageAnimationEnabled()
        ? this.applyDesktopHomepageScrollState(scrollTop).isTopReached
        : (() => {
            this.resetDesktopHomepageScrollState()
            this.resetMenuItemsTransform()
            return shouldBeScrolled
          })()

      if (shouldBeScrolled !== this.isScrolled || (shouldBeScrolled && isTopReached !== headerSection.classList.contains('header-scrolled'))) {
        this.isScrolled = shouldBeScrolled

        if (shouldBeScrolled && isTopReached) {
          headerSection.classList.add('header-scrolled')
        } else {
          headerSection.classList.remove('header-scrolled')
        }
      }

      if (shouldBeScrolled) {
        if (this.isAlwaysSticky || this.isDesktopHomepageAnimationEnabled()) {
          document.body.classList.add(this.classes.pinned)
        } else if (scrollTop < this.currentScrollTop || scrollTop < headerBoundsBottom + 100) {
          document.body.classList.add(this.classes.pinned)
        } else {
          document.body.classList.remove(this.classes.pinned)
        }
      } else {
        document.body.classList.remove(this.classes.pinned)
      }

      this.currentScrollTop = scrollTop
    })
  }

  initializeMenuItemsTransform (scrollProgress) {
    const menuList = document.querySelector('.header__menu ul')
    if (!menuList) return

    // 获取所有列表项（包括logo）
    // 只选择一级导航栏的li，排除下拉菜单中的li（含 .dropdown__nav 与 .dropdown__nav-bottom-list 内的子集）
    this.menuListItems = Array.from(menuList.querySelectorAll('li')).filter(li => {
      return !li.closest('.dropdown__nav') && !li.closest('.dropdown__nav-bottom-list')
    })

    // 查找logo索引
    this.logoIndex = this.menuListItems.findIndex(li =>
      li.classList.contains('header__logo-in-menu')
    )

    // 如果未找到logo，则不继续
    if (this.logoIndex === -1) return

    // 根据滚动进度更新transform
    this.updateMenuItemsTransform(scrollProgress)
  }

  // 根据滚动进度更新菜单项transform
  updateMenuItemsTransform (scrollProgress) {
    // 只在首页应用菜单项transform动画
    if (!this.isHomepage) return

    if (!this.menuListItems || this.logoIndex === null || this.logoIndex === -1) {
      // 如果尚未初始化，则尝试初始化
      this.initializeMenuItemsTransform(scrollProgress)
      return
    }

    // Logo两侧的菜单项（紧挨着logo的）移动距离更大，其他菜单项移动距离较小
    // scrollProgress=0时移动距离最大，scrollProgress=1时移动距离为0
    const defaultTranslateX = 8
    const logoAdjacentTranslateX = defaultTranslateX + 1

    // 同时调整padding/margin：初始状态时减小间距，滚动后恢复
    const minSpacing = 0 // 最小间距（rem）- 初始状态，更紧密
    const maxSpacing = 0 // 最大间距（rem）- 滚动后，恢复默认
    const currentSpacing = minSpacing + (maxSpacing - minSpacing) * scrollProgress

    const defaultTranslate = defaultTranslateX * (1 - scrollProgress)
    const adjacentTranslate = logoAdjacentTranslateX * (1 - scrollProgress)

    this.menuListItems.forEach((li, index) => {
      // Logo 本身：X轴不移动，Y轴transform由onScrollHandler单独处理（初始状态在菜单上方）
      // 同时调整logo的margin，让间距更紧密
      if (li.classList.contains('header__logo-in-menu')) {
        if (this.logoInMenuElement) {
          this.logoInMenuElement.style.marginLeft = `${currentSpacing}rem`
          this.logoInMenuElement.style.marginRight = `${currentSpacing}rem`
          this.logoInMenuElement.style.transition = 'none'
        }
        return
      }

      // Logo 左侧的菜单项
      if (index < this.logoIndex) {
        const translateX = index === this.logoIndex - 1 ? adjacentTranslate : defaultTranslate
        li.style.transform = `translateX(${translateX}rem)`
        li.style.marginLeft = `0px`
        li.style.transition = 'none'
      }
      // Logo 右侧的菜单项
      else if (index > this.logoIndex) {
        const translateX = index === this.logoIndex + 1 ? adjacentTranslate : defaultTranslate
        li.style.transform = `translateX(${-translateX}rem)`
        li.style.marginLeft = `0px`
        li.style.transition = 'none'
      }
    })

    if (this.headerIconsRight) {
      this.headerIconsRight.style.transform = `translateX(${-defaultTranslate}rem)`
      this.headerIconsRight.style.transition = 'none'
      this.headerIconsRight.style.willChange = scrollProgress < 1 ? 'transform' : ''
    }
  }

  // animateLogos方法已移除 - 不再对菜单中的logo进行动画处理
}

customElements.define('sticky-header', StickyHeader, { extends: 'header' })

const clearDropdownCount = new WeakMap()
class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super()
    // 初始化属性
    this.classes = { bodyClass: 'has-dropdown-menu' }
    this.events = {
      handleAfterHide: 'menu:handleAfterHide',
      handleAfterShow: 'menu:handleAfterShow',
    }

    // 引用第一个和最后一个子元素
    this.summaryElement = this.firstElementChild
    this.contentElement = this.lastElementChild

    // 基于属性的初始状态
    this._open = this.hasAttribute('open')

    // 为summary元素添加事件监听器
    this.summaryElement.addEventListener('click', this.handleSummaryClick.bind(this))

    if (this.trigger === 'hover') {
      this.summaryElement.addEventListener('focusin', (event) => {
        if (event.target === this.summaryElement) {
          this.open = true
        }
      })
      this.summaryElement.addEventListener('focusout', (event) => {
        if (!this.contentElement.contains(event.relatedTarget)) {
          this.open = false
        }
      })
    }

    // 绑定方法以确保调用时'this'上下文正确
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    this.handleEscKeyPress = this.handleEscKeyPress.bind(this)
    this.handleFocusOut = this.handleFocusOut.bind(this)
    this.handleResize = this.handleResize.bind(this)

    // 设置带防抖的悬停检测
    this.hoverTimer = null
    this.detectHoverListener = this.detectHover.bind(this)
    this.detectHoverLeaveListener = this.detectHoverLeave.bind(this)
    this.detectSubmenuLeaveListener = this.detectSubmenuLeave.bind(this)
    this.addEventListener('mouseenter', this.detectHoverListener)
    this.addEventListener('mouseleave', this.detectHoverLeaveListener)
    if (this.contentElement) {
      this.contentElement.addEventListener('mouseleave', this.detectSubmenuLeaveListener)
    }
  }

  set open (value) {
    // 检查新值是否与当前值不同
    if (value !== this._open) {
      // 更新内部状态
      this._open = value

      // 根据元素是否连接到DOM执行操作
      if (this.isConnected) {
        // 如果已连接，执行过渡
        this.transition(value)
      } else {
        // 如果未连接，直接操作'open'属性
        if (value) {
          this.setAttribute('open', '')
        } else {
          this.removeAttribute('open')
        }
      }
    }
  }

  get open () {
    return this._open
  }

  get trigger () {
    if (this.hasAttribute('trigger')) {
      return this.getAttribute('trigger')
    } else {
      return 'click'
    }
  }

  get level () {
    if (this.hasAttribute('level')) {
      return this.getAttribute('level')
    } else {
      return 'top'
    }
  }

  handleSummaryClick (event) {
    // 阻止事件的默认操作和冒泡，避免触发 handleOutsideClick
    event.preventDefault()
    event.stopPropagation()

    // 检查设备是否不支持触摸且触发器类型为'hover'
    if (this.summaryElement.hasAttribute('data-link')) {
      const summaryLink = this.summaryElement.getAttribute('data-link')
      const hasNavigableLink = summaryLink && summaryLink !== '#' && summaryLink !== '/#' && !summaryLink.toLowerCase().startsWith('javascript:')
      if (!hasNavigableLink) {
        this.open = !this.open
        return
      }
      // 如果满足条件，导航到'data-link'中指定的URL
      this.open = !this.open
    } else {
      // 如果点击的是其他菜单的 summary，先关闭所有其他菜单
      if (!this.open) {
        // 关闭所有其他打开的菜单（包括 details-dropdown 和 details-mega）
        const allDropdowns = document.querySelectorAll('details[is="details-dropdown"][open], details[is="details-mega"][open]')
        allDropdowns.forEach(dropdown => {
          if (dropdown !== this && dropdown.open) {
            // 清除其他菜单的 hover 定时器
            if (dropdown.hoverTimer) {
              clearTimeout(dropdown.hoverTimer)
              dropdown.hoverTimer = null
            }
            dropdown.open = false
          }
        })
      }
      // 切换'open'状态
      this.open = !this.open
    }
  }

  async transition (value) {
    if (value) {
      // 增加下拉菜单的锁定计数
      clearDropdownCount.set(DetailsDropdown, clearDropdownCount.get(DetailsDropdown) + 1)

      // 向body添加类并设置属性
      document.body.classList.add(this.classes.bodyClass)
      this.setAttribute('open', '')
      this.summaryElement.setAttribute('open', '')
      setTimeout(() => this.contentElement.setAttribute('open', ''), 100)

      // 添加事件监听器
      document.addEventListener('click', this.handleOutsideClick)
      document.addEventListener('keydown', this.handleEscKeyPress)
      document.addEventListener('focusout', this.handleFocusOut)
      window.addEventListener('resize', this.handleResize)

      // 在动画开始前就设置下拉菜单宽度，避免展开时有间隙
      // 使用 requestAnimationFrame 确保布局已更新
      await new Promise(resolve => requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.setHeaderDropdownTopOffset()
          this.setDropdownWidth()
          resolve()
        })
      }))

      // 执行进入动画过渡
      await this.showWithTransition()

      // 动画完成后再次确认宽度（处理可能的布局变化）
      this.setHeaderDropdownTopOffset()
      this.setDropdownWidth()

      // 检查反向条件（此处未实现以保持简洁）
      this.needsReverse()

      // 等待显示后事件
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterShow)
    } else {
      // 清除悬停定时器
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer)
        this.hoverTimer = null
      }

      // 减少下拉菜单的锁定计数，并立即释放 body 状态，让 header 背景快速恢复透明。
      clearDropdownCount.set(DetailsDropdown, Math.max(0, clearDropdownCount.get(DetailsDropdown) - 1))
      document.body.classList.toggle(this.classes.bodyClass, clearDropdownCount.get(DetailsDropdown) > 0)

      // 移除属性和事件监听器
      this.summaryElement.removeAttribute('open')
      this.contentElement.removeAttribute('open')
      document.removeEventListener('click', this.handleOutsideClick)
      document.removeEventListener('keydown', this.handleEscKeyPress)
      document.removeEventListener('focusout', this.handleFocusOut)
      window.removeEventListener('resize', this.handleResize)

      // 执行退出动画过渡
      await this.hideWithTransition()

      this.clearHeaderDropdownTopOffset()

      // 有条件地移除'open'属性
      if (!this.open) {
        this.removeAttribute('open')
      }

      // 等待隐藏后事件
      return FoxTheme.utils.waitForEvent(this, this.events.handleAfterHide)
    }
  }

  async showWithTransition () {
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: [0, 1], visibility: 'visible' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
        delay: FoxTheme.config.motionReduced ? 0 : 0.1,
      }
    )
    const translateY = this.level === 'top' ? '-105%' : '2rem'
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: [`translateY(${translateY})`, 'translateY(0)'] },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished
  }

  async hideWithTransition () {
    FoxTheme.Motion.animate(
      this.contentElement,
      { opacity: 0, visibility: 'hidden' },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.3,
        easing: [0.7, 0, 0.2, 1],
      }
    )
    const translateY = this.level === 'top' ? '-105%' : '2rem'
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      { transform: `translateY(${translateY})` },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.6,
        easing: [0.7, 0, 0.2, 1],
      }
    ).finished
  }

  handleOutsideClick (event) {
    const isClickInside = this.contains(event.target)
    const clickedDropdown = event.target.closest('details[is="details-dropdown"]')
    const clickedMega = event.target.closest('details[is="details-mega"]')
    const clickedDetails = clickedDropdown || clickedMega
    const isClickOnAnyDropdown = clickedDetails && clickedDetails !== this

    // 如果点击的是其他菜单的 summary 元素或其子元素，允许切换，不关闭当前菜单
    const isClickOnOtherSummary = clickedDetails && clickedDetails !== this &&
      clickedDetails.summaryElement &&
      (clickedDetails.summaryElement.contains(event.target) ||
        event.target === clickedDetails.summaryElement ||
        clickedDetails.summaryElement.querySelector('summary')?.contains(event.target))

    // 如果点击的是其他菜单的 summary，不关闭当前菜单（让 handleSummaryClick 处理切换）
    if (!isClickInside && !isClickOnAnyDropdown && !isClickOnOtherSummary) {
      this.open = false
    }
  }

  handleEscKeyPress (event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]')
      if (targetMenu) {
        targetMenu.open = false
      }
    }
  }

  handleFocusOut (event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false
    }
  }

  detectHover (event) {
    if (this.trigger === 'hover') {
      // 清除之前的延迟关闭定时器
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer)
        this.hoverTimer = null
      }

      // 关闭所有其他打开的下拉菜单（包括 details-dropdown 和 details-mega）
      const allDropdowns = document.querySelectorAll('details[is="details-dropdown"][open], details[is="details-mega"][open]')
      allDropdowns.forEach(dropdown => {
        if (dropdown !== this && dropdown.open) {
          // 清除其他菜单的 hover 定时器
          if (dropdown.hoverTimer) {
            clearTimeout(dropdown.hoverTimer)
            dropdown.hoverTimer = null
          }
          dropdown.open = false
        }
      })

      this.open = true
    }
  }

  detectHoverLeave (event) {
    if (this.trigger === 'hover') {
      // 检查鼠标是否移入到当前 details 或下拉菜单内容区域
      const relatedTarget = event.relatedTarget
      const isMovingToSelf = relatedTarget && (
        this.contains(relatedTarget) ||
        relatedTarget.closest('details[is="details-dropdown"]') === this ||
        relatedTarget.closest('details[is="details-mega"]') === this ||
        this.contentElement.contains(relatedTarget)
      )

      // 检查是否移入到其他下拉菜单（如果是，当前菜单应该立即关闭）
      const isMovingToOtherDropdown = relatedTarget && (
        (relatedTarget.closest('details[is="details-dropdown"]') &&
          relatedTarget.closest('details[is="details-dropdown"]') !== this) ||
        (relatedTarget.closest('details[is="details-mega"]') &&
          relatedTarget.closest('details[is="details-mega"]') !== this)
      )

      // 如果鼠标移入到当前 details 或下拉菜单内容区域，不关闭
      if (isMovingToSelf) {
        return
      }

      // 如果移入到其他下拉菜单，立即关闭当前菜单（不需要延迟）
      if (isMovingToOtherDropdown) {
        if (this.hoverTimer) {
          clearTimeout(this.hoverTimer)
          this.hoverTimer = null
        }
        this.open = false
        return
      }

      return
    }
  }

  detectSubmenuLeave (event) {
    if (this.trigger !== 'hover' || !this.open) return

    const relatedTarget = event.relatedTarget
    const isMovingBackToTrigger = relatedTarget && this.contains(relatedTarget)
    const isMovingToOtherDropdown = relatedTarget && (
      (relatedTarget.closest('details[is="details-dropdown"]') &&
        relatedTarget.closest('details[is="details-dropdown"]') !== this) ||
      (relatedTarget.closest('details[is="details-mega"]') &&
        relatedTarget.closest('details[is="details-mega"]') !== this)
    )

    if (isMovingBackToTrigger) return

    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer)
      this.hoverTimer = null
    }

    if (isMovingToOtherDropdown) {
      this.open = false
      return
    }

    this.open = false
  }

  needsReverse () {
    const totalWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2
    if (totalWidth > window.innerWidth) {
      this.contentElement.classList.add('needs-reverse')
    }
  }

  /**
   * 主导航 ul 下的一级 li（含中间插入的 logo li）。
   * 底边测量必须包含 logo：单行 + logo 高于文字时，整行高度由 logo 决定，若仍用 100%（仅 details 高度）会在菜单行与下拉之间留缝。
   */
  _getTopLevelMenuItemLis (ul) {
    return Array.from(ul.children).filter(li => {
      return !li.closest('.dropdown__nav') && !li.closest('.dropdown__nav-bottom-list')
    })
  }

  /**
   * 用一级 li 的最大底边：比 ul 盒底更贴文字（flex gap、ul 隐式高度等可能让 ul.bottom 偏下）。
   * nav 底边仍不用：含 padding / 对齐空隙。
   */
  _getVisiblePrimaryNavContentBottom (header) {
    const menus = Array.from(header.querySelectorAll('.header__menu')).filter(nav => {
      const r = nav.getBoundingClientRect()
      if (r.width <= 0 || r.height <= 0) return false
      const st = window.getComputedStyle(nav)
      if (st.display === 'none' || st.visibility === 'hidden') return false
      return true
    })
    let maxBottom = 0
    for (const nav of menus) {
      const ul = nav.querySelector(':scope > ul') || nav.querySelector('ul')
      if (!ul) {
        maxBottom = Math.max(maxBottom, nav.getBoundingClientRect().bottom)
        continue
      }
      const lis = this._getTopLevelMenuItemLis(ul)
      if (lis.length === 0) {
        maxBottom = Math.max(maxBottom, ul.getBoundingClientRect().bottom)
        continue
      }
      const bottom = Math.max(...lis.map(li => li.getBoundingClientRect().bottom))
      maxBottom = Math.max(maxBottom, bottom)
    }
    return maxBottom
  }

  setHeaderDropdownTopOffset () {
    if (this.level !== 'top' || !this.contentElement.classList.contains('dropdown')) {
      return
    }
    const header = this.closest('.header')
    if (!header || !header.querySelector('.header__menu')) {
      return
    }
    if (typeof window.matchMedia === 'function' && !window.matchMedia('(min-width: 1024px)').matches) {
      this.clearHeaderDropdownTopOffset()
      return
    }

    const headerRect = header.getBoundingClientRect()
    if (!headerRect.bottom) {
      return
    }
    const detailsRect = this.getBoundingClientRect()
    // absolute 的 top 相对 details 的 padding 边上沿；getBoundingClientRect 是 border 盒，需加上 border-top，否则大屏/高分下会剩 1px 级细缝
    let paddingEdgeTop = detailsRect.top
    if (window.getComputedStyle) {
      const bt = parseFloat(window.getComputedStyle(this).borderTopWidth) || 0
      paddingEdgeTop += bt
    }
    // 下拉面板挂在整个 header 底边，避免压到一级导航白底内部。
    const rawTop = headerRect.bottom - paddingEdgeTop
    if (rawTop < 0) {
      this.clearHeaderDropdownTopOffset()
      return
    }
    // 保留两位小数的亚像素，减轻大屏缩放 / 整像素取整带来的 1px 级细缝
    const topValue = Math.floor(rawTop * 100) / 100
    this.contentElement.style.setProperty('inset-block-start', `${topValue}px`)
    if (this.contentElement.classList.contains('dropdown--wide')) {
      this.contentElement.style.marginTop = '-1px'
    }
    this.contentElement.style.paddingBlockStart = '0px'
  }

  clearHeaderDropdownTopOffset () {
    if (!this.contentElement.classList.contains('dropdown')) {
      return
    }
    this.contentElement.style.removeProperty('inset-block-start')
    this.contentElement.style.removeProperty('margin-top')
    this.contentElement.style.removeProperty('padding-block-start')
  }

  setDropdownWidth () {
    // 只处理带有 dropdown--wide 类的下拉菜单（二级导航）
    if (!this.contentElement.classList.contains('dropdown--wide')) {
      return
    }

    const headerElement = this.closest('.header')
    if (!headerElement) {
      return
    }

    const originalTransition = this.contentElement.style.transition
    this.contentElement.style.transition = 'none'

    this.setHeaderDropdownTopOffset()

    const headerRect = headerElement.getBoundingClientRect()
    const detailsRect = this.getBoundingClientRect()

    // 背景铺满：下拉面板与 header 同宽，左缘与 header 左缘对齐
    const offsetLeft = headerRect.left - detailsRect.left
    const fullWidth = headerRect.right - headerRect.left

    if (fullWidth > 0) {
      this.contentElement.style.left = `${offsetLeft}px`
      this.contentElement.style.width = `${fullWidth}px`
      this.contentElement.style.maxWidth = `${fullWidth}px`
    }

    // 二级菜单内容与最左边第一个菜单（PRODUCTS）对齐：找到第一个菜单项的位置作为基准
    const container = this.contentElement.querySelector('.dropdown__container')
    const navColumns = this.contentElement.querySelector('.dropdown__nav--columns')
    let innerPaddingLeft = 80
    if (navColumns && window.getComputedStyle) {
      const style = window.getComputedStyle(navColumns)
      innerPaddingLeft = parseFloat(style.paddingLeft) || parseFloat(style.paddingInlineStart) || 80
    }
    // 找到第一个菜单项（PRODUCTS）的位置作为对齐基准
    let firstMenuItemLeft = headerRect.left
    const menuList = headerElement.querySelector('.header__menu ul')
    if (menuList) {
      const allMenuItems = Array.from(menuList.querySelectorAll('li')).filter(li => {
        return !li.closest('.dropdown__nav') && !li.closest('.dropdown__nav-bottom-list')
      })
      const firstMenuItem = allMenuItems[0]
      if (firstMenuItem && !firstMenuItem.classList.contains('header__logo-in-menu')) {
        const firstSummary = firstMenuItem.querySelector('summary')
        const firstMenuItemEl = firstSummary && firstSummary.querySelector('.menu__item')
        if (firstMenuItemEl && window.getComputedStyle) {
          const itemStyle = window.getComputedStyle(firstMenuItemEl)
          const itemPaddingLeft = parseFloat(itemStyle.paddingLeft) || parseFloat(itemStyle.paddingInlineStart) || 0
          firstMenuItemLeft = firstMenuItemEl.getBoundingClientRect().left + itemPaddingLeft
        } else if (firstSummary) {
          firstMenuItemLeft = firstSummary.getBoundingClientRect().left
        }
      }
    }
    if (container) {
      const contentPaddingStart = 0
      container.style.setProperty('--dropdown-content-padding-inline-start', `${Math.round(contentPaddingStart)}px`)
    }

    requestAnimationFrame(() => {
      this.contentElement.style.transition = originalTransition || ''
      // 布局稳定后再测一次，减少不同分辨率下的误差
      this._alignDropdownContentOnce()
    })

    const nestedDropdowns = this.contentElement.querySelectorAll('.dropdown .dropdown')
    nestedDropdowns.forEach(nestedDropdown => {
      const parentContainer = nestedDropdown.closest('.dropdown__container')
      if (parentContainer) {
        const containerWidth = parentContainer.offsetWidth
        nestedDropdown.style.width = `${containerWidth}px`
        nestedDropdown.style.maxWidth = `${containerWidth}px`
      }
    })
  }

  _alignDropdownContentOnce () {
    if (!this.contentElement.classList.contains('dropdown--wide')) return
    const headerElement = this.closest('.header')
    const container = this.contentElement.querySelector('.dropdown__container')
    const navColumns = this.contentElement.querySelector('.dropdown__nav--columns')
    if (!headerElement || !container || !navColumns) return

    const headerRect = headerElement.getBoundingClientRect()
    // 找到第一个菜单项（PRODUCTS）的位置作为对齐基准
    let firstMenuItemLeft = headerRect.left
    const menuList = headerElement.querySelector('.header__menu ul')
    if (menuList) {
      const allMenuItems = Array.from(menuList.querySelectorAll('li')).filter(li => {
        return !li.closest('.dropdown__nav') && !li.closest('.dropdown__nav-bottom-list')
      })
      const firstMenuItem = allMenuItems[0]
      if (firstMenuItem && !firstMenuItem.classList.contains('header__logo-in-menu')) {
        const firstSummary = firstMenuItem.querySelector('summary')
        const firstMenuItemEl = firstSummary && firstSummary.querySelector('.menu__item')
        if (firstMenuItemEl && window.getComputedStyle) {
          const itemStyle = window.getComputedStyle(firstMenuItemEl)
          const itemPaddingLeft = parseFloat(itemStyle.paddingLeft) || parseFloat(itemStyle.paddingInlineStart) || 0
          firstMenuItemLeft = firstMenuItemEl.getBoundingClientRect().left + itemPaddingLeft
        } else if (firstSummary) {
          firstMenuItemLeft = firstSummary.getBoundingClientRect().left
        }
      }
    }
    const style = window.getComputedStyle(navColumns)
    const innerPaddingLeft = parseFloat(style.paddingLeft) || parseFloat(style.paddingInlineStart) || 80
    const contentPaddingStart = 0
    container.style.setProperty('--dropdown-content-padding-inline-start', `${Math.round(contentPaddingStart)}px`)
  }

  handleResize () {
    if (!this.open) return
    requestAnimationFrame(() => {
      this.setHeaderDropdownTopOffset()
      if (this.contentElement.classList.contains('dropdown--wide')) {
        this.setDropdownWidth()
      }
    })
  }
}

customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' })
clearDropdownCount.set(DetailsDropdown, 0)

class DetailsMega extends DetailsDropdown {
  constructor() {
    super()
    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        this.open = true
      })

      this.addEventListener('shopify:block:deselect', () => {
        this.open = false
      })
    }
  }

  async showWithTransition () {
    // 在内容元素的第一个子元素上执行动画
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'visible',
        transform: ['translateY(-100%)', 'translateY(0)'],
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished
  }

  async hideWithTransition () {
    return FoxTheme.Motion.animate(
      this.contentElement.firstElementChild,
      {
        visibility: 'hidden',
        transform: 'translateY(-100%)',
      },
      {
        duration: FoxTheme.config.motionReduced ? 0 : 0.5,
        easing: [0.39, 0.575, 0.565, 1.0],
      }
    ).finished
  }
}

customElements.define('details-mega', DetailsMega, { extends: 'details' })

class MenuDrawerDetails extends HTMLDetailsElement {
  constructor() {
    super()

    this.summary.addEventListener('click', this.onSummaryClick.bind(this))
  }

  get parent () {
    return this.closest('[data-parent]')
  }

  get summary () {
    return this.querySelector('summary')
  }

  get closeButton () {
    return this.querySelector('.menu-drawer__item-back-link')
  }

  toggleSplitSummary () {
    const expandBtn = this.summary.querySelector('.menu-drawer__item-expand')
    if (this.open) {
      this.onCloseButtonClick()
      return
    }
    this.open = true
    setTimeout(() => {
      this.parent.classList.add('active')
      this.classList.add('active')
      this.summary.setAttribute('aria-expanded', 'true')
      if (expandBtn) expandBtn.setAttribute('aria-expanded', 'true')
      this.updateChevron()
    }, 100)
  }

  onSummaryClick (e) {
    if (this.summary.classList.contains('menu-drawer__summary--split')) {
      if (e.target.closest('a') || e.target.closest('.menu-drawer__item-expand')) return
      e.preventDefault()
      this.toggleSplitSummary()
      return
    }
    if (this.getAttribute('level') === 'top') {
      if (e.target === this.summary) {
        e.preventDefault()
        this.toggleSplitSummary()
      }
      return
    }
    setTimeout(() => {
      this.parent.classList.add('active')
      this.classList.add('active')
      this.summary.setAttribute('aria-expanded', 'true')
      this.updateChevron()
    }, 100)
  }

  onCloseButtonClick () {
    this.parent.classList.remove('active')
    this.classList.remove('active')
    this.summary.setAttribute('aria-expanded', 'false')
    const expandBtn = this.summary.querySelector('.menu-drawer__item-expand')
    if (expandBtn) expandBtn.setAttribute('aria-expanded', 'false')
    this.updateChevron()

    this.closeAnimation()
  }

  updateChevron () {
    const chevronContainer = this.summary.querySelector('.menu-drawer__chevron')
    if (chevronContainer) {
      const chevronDown = chevronContainer.querySelector('.icon-chevron-down')
      const chevronUp = chevronContainer.querySelector('.icon-chevron-up')

      if (this.hasAttribute('open')) {
        if (chevronDown) chevronDown.style.display = 'none'
        if (chevronUp) chevronUp.style.display = 'block'
      } else {
        if (chevronDown) chevronDown.style.display = 'block'
        if (chevronUp) chevronUp.style.display = 'none'
      }
    }
  }

  connectedCallback () {
    if (this.hasAttribute('open')) {
      this.updateChevron()
    }

    if (!this._backButtonBound && this.closeButton) {
      this._backButtonBound = true
      this.closeButton.addEventListener('click', this.onCloseButtonClick.bind(this))
    }

    if (!this._splitExpandBound) {
      const expand = this.summary.querySelector('.menu-drawer__item-expand')
      if (expand) {
        this._splitExpandBound = true
        expand.addEventListener('click', (ev) => {
          ev.preventDefault()
          ev.stopPropagation()
          this.toggleSplitSummary()
        })
      }
    }

    this.addEventListener('toggle', () => {
      this.updateChevron()
    })
  }

  closeAnimation () {
    let animationStart

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time
      }

      const elapsedTime = time - animationStart

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation)
      } else {
        this.removeAttribute('open')
      }
    }

    window.requestAnimationFrame(handleAnimation)
  }
}
customElements.define('menu-drawer-details', MenuDrawerDetails, { extends: 'details' })

class MenuDrawer extends DrawerComponent {
  constructor() {
    super()
  }
  get menuItems () {
    if (!this._menuItems) {
      this._menuItems = this.querySelectorAll('.menu-drawer__menus:not(.active) > li')
    }
    return this._menuItems
  }

  prepareToShow () {
    super.prepareToShow()
    setTimeout(() => {
      this.animateMenuItems()
    }, 300)
  }

  animateMenuItems () {
    FoxTheme.Motion.animate(
      this.menuItems,
      {
        transform: ['translateX(-20px)', 'translateX(0)'],
        opacity: [0, 1],
      },
      {
        duration: 0.6,
        easing: [0.075, 0.82, 0.165, 1],
        delay: FoxTheme.Motion.stagger(0.1),
      }
    ).finished.then(() => {
      this.menuItems.forEach((item) => item.removeAttribute('style'))
    })
  }

  handleAfterHide () {
    super.handleAfterHide()
    setTimeout(() => {
      this.querySelectorAll('details[is=menu-drawer-details]').forEach((menu) => {
        menu.onCloseButtonClick()
      })
    })
  }
}
customElements.define('menu-drawer', MenuDrawer)

function setPromoSliderPage (slider, page) {
  const totalPages = parseInt(slider.style.getPropertyValue('--promo-pages') || slider.getAttribute('data-promo-pages') || '1', 10)
  const pageNum = Math.max(1, Math.min(parseInt(page, 10) || 1, totalPages))
  slider.setAttribute('data-current-page', String(pageNum))
  slider.style.setProperty('--promo-current-page', String(pageNum))
  slider.querySelectorAll('.menu-drawer__promo-dot').forEach((d) => {
    const isActive = d.getAttribute('data-page') === String(pageNum)
    d.classList.toggle('is-active', isActive)
    d.setAttribute('aria-selected', isActive ? 'true' : 'false')
    d.setAttribute('tabindex', isActive ? '0' : '-1')
  })
}

document.addEventListener('click', (e) => {
  const dot = e.target.closest('.menu-drawer__promo-dot')
  if (!dot) return
  const slider = dot.closest('.menu-drawer__promo-slider')
  if (!slider) return
  const page = dot.getAttribute('data-page')
  setPromoSliderPage(slider, page)
})

document.addEventListener('touchstart', (e) => {
  const slider = e.target.closest('.menu-drawer__promo-slider')
  if (!slider) return
  slider._promoTouchStartX = e.touches && e.touches[0] ? e.touches[0].clientX : null
}, { passive: true })

document.addEventListener('touchend', (e) => {
  const slider = e.target.closest('.menu-drawer__promo-slider')
  if (!slider || slider._promoTouchStartX == null) return
  const diff = e.changedTouches[0].clientX - slider._promoTouchStartX
  const totalPages = parseInt(slider.style.getPropertyValue('--promo-pages') || slider.getAttribute('data-promo-pages') || '1', 10)
  const current = parseInt(slider.getAttribute('data-current-page') || '1', 10)
  if (diff < -50 && current < totalPages) setPromoSliderPage(slider, current + 1)
  else if (diff > 50 && current > 1) setPromoSliderPage(slider, current - 1)
  slider._promoTouchStartX = null
}, { passive: true })

class MenuProductList extends HTMLElement {
  constructor() {
    super()
    this.initSlide()
    this.elementToFocus = this.querySelector('button')
  }

  get container () {
    return this.querySelector('.swiper')
  }

  get sliderPagination () {
    return this.querySelector('.swiper-pagination')
  }

  get sliderNext () {
    return this.querySelector('.swiper-button-next')
  }

  get sliderPrev () {
    return this.querySelector('.swiper-button-prev')
  }

  get numberOfColumns () {
    return parseInt(this.dataset.columns)
  }

  initSlide () {
    const slider = new FoxTheme.Carousel(this.container, {
      spaceBetween: 10,
      slidesPerView: this.numberOfColumns,
      loop: false,
      pagination: {
        el: this.sliderPagination,
        type: 'fraction',
      },
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
      focusableElements: 'input, select, option, textarea, button, video, label, a',
    })
    slider.init()

    const focusableElements = FoxTheme.a11y.getFocusableElements(this)

    focusableElements.forEach((element) => {
      element.addEventListener('focusin', function () {
        const slide = element.closest('.swiper-slide')

        slide && slider.slider.slideTo(slider.slider.slides.indexOf(slide))
      })
    })
  }
}
customElements.define('menu-product-list', MenuProductList)
